import React from 'react'
import classNames from 'classnames'
import { Button, Notification } from '@deal/components'
import numeral from 'numeral'
import { Link } from 'react-router-dom'
import { DateTime } from 'luxon'
import { minutesToHoursMinutes } from '#src/services/time'
import styles from './styles.css'
import {
  ExpertCuratedOrderLineItemFragment,
  ExpertCuratedOrderFragment,
  ExpertCuratedOrderState,
  OrderState,
  FlightPlanLegFragment
} from '#src/generated/graphql'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'

interface BookingProps {
  expertCuratedOrder: ExpertCuratedOrderFragment
  sellableHasNoImages?: boolean
}

interface LineItemProps {
  lineItem: ExpertCuratedOrderLineItemFragment
}

interface FlightLegProps {
  leg: FlightPlanLegFragment
}

interface ActionLabelProps {
  state: OrderState
  inPreview: boolean
}

const ActionLabel: React.SFC<ActionLabelProps> = ({ state, inPreview }) => {
  if (inPreview) {
    return <>Preview only</>
  } else {
    switch (state) {
      case OrderState.CONSUMER_OVERPAID:
      case OrderState.CONSUMER_PAYMENT_COMPLETED:
      case OrderState.CONSUMER_PAYMENT_PARTIAL:
      case OrderState.CONSUMER_PAYMENT_PENDING:
      case OrderState.CONSUMER_PAYMENT_REFUNDED:
        return <>View receipt</>

      default:
        return <>Book now</>
    }
  }
}

const SingleLineItem: React.SFC<LineItemProps> = ({ lineItem }) => {
  if (
    lineItem.source.__typename === 'OrderLineItemGratuitySource' ||
    lineItem.source.__typename === 'OrderLineItemSellableSource' ||
    lineItem.source.__typename === 'OrderLineItemBasePriceSource'
  ) {
    if (lineItem.isGroup) {
      return null
    }

    return (
      <div key={lineItem.id}>
        <div className={styles.lineItem}>
          <span className={styles.lineItemTitle}>{lineItem.title}</span>
          <span className={styles.lineItemPrice}>
            {lineItem.unitPrice ? (
              numeral(lineItem.unitPrice.amount).format('$0,0.00')
            ) : (
              <span className={styles.free}>Free</span>
            )}
            {lineItem.quantity > 1 && `x ${lineItem.quantity}`}
          </span>
        </div>
      </div>
    )
  }

  return null
}

const FlightLeg: React.SFC<FlightLegProps> = ({ leg }) => {
  const localDepartureTime =
    leg.departAt &&
    leg.departureAirport &&
    DateTime.fromISO(leg.departAt, { zone: leg.departureAirport.zoneId })

  const localArrivalTime =
    leg.arriveAt &&
    leg.arrivalAirport &&
    DateTime.fromISO(leg.arriveAt, { zone: leg.arrivalAirport.zoneId })

  const formattedTime = minutesToHoursMinutes(leg.durationInMinutes)
  const durationLine = `${formattedTime.hours}h ${formattedTime.minutes}m`

  return (
    <div className={styles.leg}>
      <div className={styles.legDataPoint}>
        {localDepartureTime && (
          <>
            <strong>{localDepartureTime.toFormat('L/dd')}</strong>
            <span className={styles.light}>{localDepartureTime.toFormat('ccc')}</span>
          </>
        )}
      </div>

      <div className={styles.legDataPoint}>
        <strong>{localDepartureTime && localDepartureTime.toFormat('h:mm a')}</strong>
        <span className={styles.light}>
          {leg.departureAirport && leg.departureAirport.displayCode}
        </span>
      </div>

      <div className={styles.legDuration}>{durationLine}</div>

      <div className={styles.legDataPoint}>
        {localArrivalTime && (
          <>
            <strong>{localArrivalTime.toFormat('L/dd')}</strong>
            <span className={styles.light}>{localArrivalTime.toFormat('ccc')}</span>
          </>
        )}
      </div>

      <div className={styles.legDataPoint}>
        <strong>{localArrivalTime && localArrivalTime.toFormat('h:mm a')}</strong>
        <span className={styles.light}>{leg.arrivalAirport && leg.arrivalAirport.displayCode}</span>
      </div>
    </div>
  )
}

const Booking: React.SFC<BookingProps & WithAnalyticsProps> = ({
  expertCuratedOrder,
  sellableHasNoImages,
  analytics
}) => {
  const orderLineItems = expertCuratedOrder.order.lineItems
  const minimumDeposit = expertCuratedOrder.order.minimumDeposit

  if (expertCuratedOrder.state !== ExpertCuratedOrderState.ACTIVE) {
    return (
      <Notification type="error" className={styles.container}>
        This offer is no longer available. Please reach out to your expert for help.
      </Notification>
    )
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.containerWithoutImages]: sellableHasNoImages
      })}
    >
      <div className={styles.headline}>Book your charter</div>

      <div className={styles.subheader}>Charter overview</div>

      {expertCuratedOrder.flightPlan &&
        expertCuratedOrder.flightPlan.legs.map(leg => {
          return <FlightLeg leg={leg} key={leg.id} />
        })}

      <div className={styles.pricing}>
        <div className={styles.pricingHeader}>Your charter</div>
        {orderLineItems.map(orderLineItem => (
          <SingleLineItem key={orderLineItem.id} lineItem={orderLineItem} />
        ))}

        <div className={styles.lineItem}>
          <div className={styles.lineItemTitle}>
            <strong>Total</strong>
          </div>
          <div className={styles.lineItemPrice}>
            {numeral(expertCuratedOrder.order.remainingBalance.amount).format('$0,0.00')}
          </div>
        </div>

        {minimumDeposit && (
          <div className={styles.securityDepositLineItem}>
            <div className={styles.lineItemTitle}>Due Today (Deposit)</div>
            <div className={styles.lineItemPrice}>
              {numeral(minimumDeposit.amount).format('$0,0.00')}
            </div>
          </div>
        )}
      </div>
      <div className={styles.wrapper}>
        <Link to={`/orders/${expertCuratedOrder.order.id}`}>
          <Button
            className={styles.requestToBook}
            disabled={expertCuratedOrder.inPreview}
            onClick={() => {
              if (analytics) {
                analytics.expertCuratedOfferCtaClicked()
              }
            }}
          >
            <ActionLabel
              state={expertCuratedOrder.order.state}
              inPreview={expertCuratedOrder.inPreview}
            />
          </Button>
        </Link>
      </div>
    </div>
  )
}

export default withAnalytics(Booking)
